import React, { useEffect, useRef, useState, useMemo } from "react";
import { mccCodes, formatMcc, type MccCode } from "@cpos/shared";
import { useClickOutside } from "@/hooks/useClickOutside";
import { selectClass } from "./styles";

const PINNED_MCC = ["5812", "5411", "5999", "7299", "7399", "8999"];

const pinnedOptions = PINNED_MCC.map((code) =>
  mccCodes.find((c) => c.value === code),
).filter(Boolean) as MccCode[];

interface MccComboboxProps {
  id?: string;
  value: string;
  onChange: (code: string) => void;
  error?: string;
}

export function MccCombobox({ id, value, onChange, error }: MccComboboxProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const filtered = useMemo(() => {
    if (!query) return [];
    const q = query.toLowerCase();
    const pinnedMatches: MccCode[] = [];
    const rest: MccCode[] = [];
    for (const opt of mccCodes) {
      const matches =
        opt.value.toLowerCase().includes(q) ||
        opt.label.toLowerCase().includes(q);
      if (!matches) continue;
      if (PINNED_MCC.includes(opt.value)) {
        pinnedMatches.push(opt);
      } else {
        rest.push(opt);
      }
    }
    return [...pinnedMatches, ...rest].slice(0, 50);
  }, [query]);

  const items = query ? filtered : pinnedOptions;
  const showDivider = !query;

  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const offset = showDivider ? 1 : 0;
      const el = listRef.current.children[activeIndex + offset] as
        HTMLElement | undefined;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, showDivider]);

  useClickOutside(containerRef, () => setOpen(false));

  const select = (opt: MccCode) => {
    onChange(opt.value);
    setQuery("");
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || items.length === 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
        setActiveIndex(0);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i < items.length - 1 ? i + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i > 0 ? i - 1 : items.length - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      // eslint-disable-next-line security/detect-object-injection
      if (activeIndex >= 0) select(items[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const displayValue = formatMcc(value);

  return (
    <div ref={containerRef} className="relative">
      <input
        id={id}
        type="text"
        value={open ? query : displayValue}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActiveIndex(-1);
        }}
        onFocus={() => {
          setQuery("");
          setOpen(true);
          setActiveIndex(-1);
        }}
        onKeyDown={handleKeyDown}
        placeholder="Search MCC code or industry..."
        className={selectClass(error)}
        autoComplete="off"
        data-1p-ignore
        data-lpignore="true"
      />
      {open && items.length > 0 && (
        <ul
          ref={listRef}
          className="border-border bg-card absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-lg border shadow-lg"
        >
          {showDivider && (
            <li className="text-subtle-foreground px-3 py-1.5 text-xs font-semibold tracking-wide uppercase select-none">
              Most Used
            </li>
          )}
          {items.map((opt, i) => (
            <li
              key={opt.value}
              onMouseDown={() => select(opt)}
              onMouseEnter={() => setActiveIndex(i)}
              className={`cursor-pointer px-3 py-2 text-sm transition-colors ${
                i === activeIndex
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              <span className="text-foreground font-medium">{opt.value}</span>
              {" — "}
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
